import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';

const createVehicleIcon = (status) => {
  const color = status === 'sos' ? '#ff1744' : status === 'active' ? '#00e676' : '#666';
  return L.divIcon({
    className: '',
    html: `<div style="
      background: ${color};
      width: 14px; height: 14px;
      border-radius: 50%;
      border: 2px solid #fff;
      box-shadow: 0 0 8px ${color};
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
};

const SIGN_ICONS = {
  speed_limit: '🚫',
  danger: '⚠️',
  curve: '↩️',
  slippery: '💧',
  fog: '🌫️',
  construction: '🚧',
  checkpoint: '🛑',
  accident_zone: '💀',
  school: '🏫',
  pedestrian_crossing: '🚶',
  stop: '🛑',
  yield: '🔺',
  no_entry: '⛔',
  one_way: '➡️',
  speed_bump: '⏫',
  traffic_light: '🚦',
  roundabout: '🔄',
  narrow_road: '↔️',
  animal_crossing: '🦌',
  no_overtaking: '🚫',
  no_parking: '🅿️',
  hospital: '🏥',
  police: '👮',
  fuel_station: '⛽',
  bridge: '🌉',
  tunnel: '🚇',
  falling_rocks: '🪨',
  flood_zone: '🌊',
  custom: '📍',
};

const ALERT_COLORS = {
  accident: '#ff1744', traffic: '#ff9100', breakdown: '#ffea00',
  road_closed: '#d50000', obstacle: '#ff6d00', rain: '#2979ff',
  danger: '#ff1744', other: '#888',
};

const createSignIcon = (kind) => {
  return L.divIcon({
    className: '',
    html: `<div style="
      font-size: 18px; line-height: 1;
      background: rgba(26,26,46,0.9);
      border: 1px solid #ff9100;
      border-radius: 6px;
      padding: 2px 4px;
      text-align: center;
    ">${SIGN_ICONS[kind] || '📍'}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

function FlyToVehicle({ vehicleId, vehicles }) {
  const map = useMap();
  useEffect(() => {
    if (vehicleId && vehicles[vehicleId]) {
      const v = vehicles[vehicleId];
      if (v.lat && v.lng) {
        map.flyTo([v.lat, v.lng], 16, { duration: 1 });
      }
    }
  }, [vehicleId, vehicles, map]);
  return null;
}

export function VehicleMarker({ vehicle, onClick }) {
  const v = vehicle;
  if (!v.lat || !v.lng) return null;

  return (
    <Marker
      position={[v.lat, v.lng]}
      icon={createVehicleIcon(v.status || 'active')}
      eventHandlers={{ click: () => onClick && onClick(v.vehicle_id) }}
    >
      <Popup>
        <strong>{v.plate}</strong><br />
        {v.driver_name}<br />
        Vitesse: <strong>{Math.round(v.speed || 0)} km/h</strong><br />
        {v.status === 'sos' && <span style={{ color: 'red', fontWeight: 'bold' }}>⚠️ SOS ACTIF</span>}
      </Popup>
    </Marker>
  );
}

function SignMarker({ sign }) {
  if (!sign.lat || !sign.lng) return null;
  return (
    <Marker position={[sign.lat, sign.lng]} icon={createSignIcon(sign.kind)}>
      <Popup>
        <strong>{sign.label || sign.kind}</strong><br />
        {sign.value && `${sign.value} km/h`}<br />
        Rayon: {sign.radius_m}m
      </Popup>
    </Marker>
  );
}

function AlertMarker({ alert }) {
  if (!alert.lat || !alert.lng) return null;
  const color = ALERT_COLORS[alert.kind] || '#888';
  return (
    <CircleMarker
      center={[alert.lat, alert.lng]}
      radius={10}
      pathOptions={{ color, fillColor: color, fillOpacity: 0.6, weight: 2 }}
    >
      <Popup>
        <strong style={{ color }}>{alert.kind}</strong><br />
        {alert.description || ''}<br />
        Statut: {alert.status}<br />
        {alert.reporter_plate && `Véhicule: ${alert.reporter_plate}`}
      </Popup>
    </CircleMarker>
  );
}

export function LiveMap({
  vehicles, signs = [], alerts = [],
  showVehicles = true, showSigns = true, showAlerts = true,
  focusVehicleId, center, zoom, onVehicleClick, children,
}) {
  return (
    <MapContainer
      center={center || [-4.325, 15.322]}
      zoom={zoom || 12}
      style={{ width: '100%', height: '100%', borderRadius: 12 }}
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; OSM'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FlyToVehicle vehicleId={focusVehicleId} vehicles={vehicles} />
      {showVehicles && Object.values(vehicles).map((v) => (
        <VehicleMarker key={v.vehicle_id} vehicle={v} onClick={onVehicleClick} />
      ))}
      {showSigns && signs.map((s) => (
        <SignMarker key={`sign-${s.id}`} sign={s} />
      ))}
      {showAlerts && alerts.map((a) => (
        <AlertMarker key={`alert-${a.id}`} alert={a} />
      ))}
      {children}
    </MapContainer>
  );
}
